import {
  signUp,
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  getCurrentUser,
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
} from 'aws-amplify/auth';
import { apiGet, apiPost, apiPut, apiPostMultipart, apiDelete } from '@/lib/api';
import biometricAuthService from "@/services/biometricAuthService";
import { logger } from "./logger";
import ENV from "./env";

interface AuthResponse {
  user: any | null; // Loosened type for migration
  session: any | null;
  error: any | null;
  retryable: boolean;
  requiresEmailConfirmation?: boolean;
}

interface AuthServiceType {
  signUp: (
    email: string,
    password: string,
    userData?: any,
  ) => Promise<AuthResponse>;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => Promise<{ error: any | null }>;
  resetPassword: (email: string) => Promise<{ error: any | null }>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<{ error: any | null }>;
  updatePassword: (newPassword: string, oldPassword?: string) => Promise<{ error: any | null }>;
  getCurrentUser: () => Promise<any | null>;
  getSession: () => Promise<any | null>;
  getProfile: (userId?: string) => Promise<any | null>;
  updateProfile: (userId: string, profileData: any) => Promise<any | null>;
  getRoles: () => Promise<string[]>;
  getDashboardSummary: () => Promise<any | null>;
  getUnrespondedPatients: () => Promise<any[]>;
  markPatientContacted: (encounterId: string) => Promise<any>;
  getAppointmentConfirmInfo: (token: string) => Promise<any>;
  submitAppointmentConfirmation: (token: string, action: string) => Promise<any>;
  getAppointments: (params: any) => Promise<any>;
  createAppointments: (appointments: any[]) => Promise<any>;
  getPatients: (search?: string) => Promise<any[]>;
  getHospitalsAndBranches: () => Promise<any>;
  getPublicHospitalsAndBranches: () => Promise<any>;
  createUserProfile: (userId: string, userData: any) => Promise<void>;
  checkUserExists: (email: string) => Promise<boolean>;
  confirmSignUp: (email: string, code: string) => Promise<{ error: any | null }>;
  resendSignUpCode: (email: string) => Promise<{ error: any | null }>;
  getQuestionnaireResponses: (params: any) => Promise<any[]>;
  getQuestionnaireTrend: (patientId: string, questionnaireType: string) => Promise<any[]>;
  verifyQuestionnaireAccess: (code: string) => Promise<any>;
  getPublicPatient: (patientId: string, accessCode: string) => Promise<any | null>;
  savePublicConsent: (patientId: string, accessCode: string, consentEntries: any[]) => Promise<any>;
  getStaffByRole: (role: string) => Promise<any[]>;
  getPatientProviders: (patientId: string) => Promise<any[]>;
  submitQuestionnaireResponse: (data: any) => Promise<any>;
  updateQuestionnaireResponse: (id: string, data: any) => Promise<any>;
  getPatient: (id: string) => Promise<any | null>;
  createSMSLog: (logs: any[]) => Promise<any>;
  checkSMSUniquePageId: (id: string) => Promise<boolean>;
  getSMSLogCount: (patientId: string) => Promise<number>;
  updateSMSLog: (uniqueId: string, data: any) => Promise<any>;
  // RDS migration - additional endpoints (replacing Supabase)
  updatePatient: (id: string, data: any) => Promise<any>;
  createPatient: (data: any) => Promise<any>;
  importPatients: (patients: any[]) => Promise<void>;
  getPatientsWithFilters: (params: {
    location_id?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    patientIds?: string[];
  }) => Promise<{ data: any[]; total?: number }>;
  getPatientEncounters: (params: {
    patient_id?: string;
    primary_provider_id?: string;
    secondary_provider_id?: string;
  }) => Promise<any[]>;
  createPatientEncounter: (data: any) => Promise<any>;
  updatePatientEncounter: (encounterId: string, data: any) => Promise<any>;
  getSMSLogsByPatient: (patientId: string) => Promise<any[]>;
  getQuestionnaireTemplates: (params?: { ids?: string[]; is_active?: boolean }) => Promise<any[]>;
  sendResendQuestionnaireEmail: (body: {
    type: string;
    form_data: any;
    total_score: number;
    questions: any[];
  }) => Promise<void>;
  getTokenQuestionnaire: (token: string) => Promise<any | null>;
  getRolesMetadata: () => Promise<any[]>;
  uploadPatientCSV: (file: File) => Promise<any>;
  getStates: () => Promise<{ state_id: string; state_name: string }[]>;
  createHospital: (data: any) => Promise<any>;
  updateHospital: (id: string, data: any) => Promise<any>;
  createBranch: (hospitalId: string, data: any) => Promise<any>;
  updateBranch: (branchId: string, data: any) => Promise<any>;
  // Call management RDS endpoints
  checkCallLog: (questionnaireId?: string, patientId?: string, fetchAll?: boolean) => Promise<any>;
  scheduleCall: (data: { patient_id: string; scheduled_at: string; questionnaire_id?: string; notes?: string }) => Promise<any>;
  callNow: (data: { patient_id: string; questionnaire_id?: string; notes?: string }) => Promise<any>;
  fetchCallNotes: (callLogId: number, manualNotes?: string) => Promise<any>;
  uploadCallRecording: (audio: File, callLogId: number, manualNotes?: string) => Promise<any>;
  getCallRecording: (callLogId: number, presignSeconds?: number) => Promise<any>;
  verifyCallAccess: (code: string) => Promise<any>;
  updateCallConsent: (code: string, consent: 'YES' | 'NO') => Promise<any>;
  createStaff: (data: any) => Promise<any>;
  updateStaff: (id: string, data: any) => Promise<any>;
  getProviders: (params?: { location_id?: string | number; branch_id?: string | number }) => Promise<any[]>;
  getBranchByLocation: (locationId: string | number) => Promise<any>;
  updateMyProfile: (data: any) => Promise<any>;
  // Audio Archive RDS endpoints
  listAudioFiles: (params?: {
    date?: string;
    survey_reference?: string;
    location_id?: string | number;
    branch_id?: string;
    primary_cp_uuid?: string;
    secondary_cp_uuid?: string;
    patient_uuid?: string;
  }) => Promise<any>;
  getPatientAudioFiles: (patientUuid: string, params?: { date?: string; survey_reference?: string }) => Promise<any>;
  refreshAudioPresignUrl: (key: string, expiration?: number) => Promise<any>;
  uploadAudioFile: (patientUuid: string, file: File, surveyReference: string) => Promise<any>;
  deleteAudioFile: (patientUuid: string, s3Key: string) => Promise<any>;
  // CMS Quality Alerts
  getCmsAlertStats: (params?: { days?: number }) => Promise<any>;
  getActiveCmsAlerts: (params: {
    patient_id?: string;
    cp_id?: string;
    severity?: string;
    questionnaire_type?: string;
    alert_status?: string;
    unbilled?: string;
    page?: number;
    per_page?: number;
  }) => Promise<any>;
  getPatientCmsAlerts: (patientId: string) => Promise<any[]>;
  acknowledgeCmsAlert: (alertId: number) => Promise<any>;
  dismissCmsAlert: (alertId: number) => Promise<any>;
  resolveCmsAlert: (alertId: number, notes: string) => Promise<any>;
  billCmsAlert: (alertId: number) => Promise<any>;
  // Registration Queue & Lead Self-Registration
  generateRegistrationLink: (params: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    send_sms?: boolean;
    send_email?: boolean;
    base_url?: string;
  }) => Promise<any>;
  getPublicRegistrationData: (token: string) => Promise<any>;
  submitPublicRegistration: (token: string, patient_data: any) => Promise<any>;
  getRegistrationQueue: (status?: string, search?: string) => Promise<any>;
  getRegistrationQueueItem: (id: string) => Promise<any>;
  resendRegistrationLink: (id: string, options?: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    send_sms?: boolean;
    send_email?: boolean;
    preserve_data?: boolean;
  }) => Promise<any>;
  completeRegistrationQueueItem: (id: string, final_patient_id: string) => Promise<any>;
}

const AuthService: AuthServiceType = {
  async checkUserExists(email: string): Promise<boolean> {
    try {
      const responseBody = await apiGet('/rds/staff-users/me', { email });
      return !!responseBody;
    } catch (error) {
      console.error("Error checking if user exists:", error);
      return false;
    }
  },

  async signUp(
    email: string,
    password: string,
    userData: any = {},
  ): Promise<AuthResponse> {
    logger.info("🔑 Starting signup process for:", email);

    try {
      const { userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: userData.fullName, // Standard attribute
          },
          autoSignIn: true,
        }
      });

      console.log("✅ Sign up successful", userId);

      // Create profile in DB
      if (userId) {
        this.createUserProfile(userId, { ...userData, email }).catch(console.error);
      }

      return {
        user: { id: userId, username: userId, email },
        session: null,
        error: null,
        retryable: false, // Default to false
        requiresEmailConfirmation: nextStep.signUpStep === 'CONFIRM_SIGN_UP'
      };

    } catch (error: any) {
      console.error("❌ Signup failed:", error);
      return {
        user: null,
        session: null,
        error: error,
        retryable: false, // Simplify for now
      };
    }
  },

  async confirmSignUp(email: string, code: string) {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async resendSignUpCode(email: string) {
    try {
      await resendSignUpCode({ username: email });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      if (isSignedIn) {
        const user = await getCurrentUser();

        // TOKEN EXCHANGE: Get a backend-trusted token using the Cognito Username (migrated auth_uuid)
        try {
          logger.info("🔄 Exchanging Cognito session for backend token...");
          // We pass true for staff_login to get a token with appropriate roles/permissions
          // IMPORTANT: Using user.username as it represents the migrated auth_uuid/identifier
          await biometricAuthService.login(user.username, true);
          logger.info("✅ Token exchange successful");
        } catch (err) {
          logger.error("❌ Token exchange failed:", err);
        }

        const session = await fetchAuthSession();
        return {
          user: { id: user.username, username: user.username, email },
          session: session,
          error: null,
          retryable: false
        };
      } else if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        // Cognito account exists but email not yet verified.
        // Return requiresEmailConfirmation so Login.tsx redirects to /verify-email
        // rather than showing a raw technical error message.
        return {
          user: null,
          session: null,
          error: null,
          retryable: false,
          requiresEmailConfirmation: true,
        };
      } else {
        return {
          user: null,
          session: null,
          error: { message: "Sign in required next step: " + nextStep.signInStep },
          retryable: false
        };
      }

    } catch (error: any) {
      console.error("Login error:", error);

      // A stale Amplify session is cached (e.g. after a hard refresh or tab restore).
      // Sign out silently and retry once — this is transparent to the user.
      if (error?.name === "UserAlreadyAuthenticatedException") {
        logger.warn("⚠️ Stale Amplify session detected — signing out and retrying...");
        try {
          await signOut({ global: false });
        } catch (signOutErr) {
          logger.warn("Sign-out during stale-session recovery failed:", signOutErr);
        }
        // Retry sign-in once after clearing the stale session
        return this.signIn(email, password);
      }

      // Cognito's NotAuthorizedException message intentionally reveals whether
      // the user exists ("Incorrect username or password" vs "User does not exist").
      // Replace with a generic message to avoid user enumeration.
      const safeError = {
        ...error,
        message: "Your email or password is incorrect. Please check your credentials and try again.",
      };
      return { user: null, session: null, error: safeError, retryable: false };
    }
  },

  async signOut() {
    try {
      sessionStorage.removeItem('mfa_verified');
      await signOut();
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async resetPassword(email: string) {
    try {
      await resetPassword({ username: email });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async confirmResetPassword(email: string, code: string, newPassword: string) {
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async updatePassword(newPassword: string, oldPassword?: string) {
    try {
      if (!oldPassword) {
        return { error: { message: "Old password is required for security updates." } };
      }
      await updatePassword({ newPassword, oldPassword });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return user; // Returns AuthUser (userId, username)
    } catch {
      return null;
    }
  },

  async getSession() {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch {
      return null;
    }
  },

  async getProfile(userId?: string) {
    try {
      let id = userId;
      if (!id) {
        try {
          const user = await getCurrentUser();
          id = user.username; // Default to username (UUID)
        } catch {
          console.warn("No current user to get profile for");
          return null;
        }
      }

      const isEmail = id.includes('@');

      // If it's an email, use /rds/staff-users/me?email=...
      // Otherwise use /rds/staff-users/:id
      const endpoint = isEmail ? '/rds/staff-users/me' : `/rds/staff-users/${id}`;
      const params = isEmail ? { email: id } : {};

      logger.debug(`🔍 Fetching RDS profile at ${endpoint}`, params);
      const profile = await apiGet(endpoint, params);

      if (profile && !profile.role && profile.staff_role) {
        profile.role = profile.staff_role;
      }

      return profile;
    } catch (error: any) {
      console.error(`❌ Error getting profile for ${userId || 'current user'}:`, error);
      return null;
    }
  },

  async updateProfile(userId: string, profileData: any) {
    try {
      return await apiPost(`/rds/staff-users/${userId}/update`, profileData);
    } catch (error) {
      console.error("Error updating profile:", error);
      return { error };
    }
  },

  async getRoles(): Promise<string[]> {
    try {
      const res = await apiGet('/rds/lookups/roles');
      logger.debug('🔍 [getRoles] response:', res);

      const rolesData = Array.isArray(res)
        ? res
        : Array.isArray(res?.roles)
          ? res.roles
          : null;

      if (!rolesData) {
        logger.warn('⚠️ [getRoles] Unexpected API shape, returning empty list.', res);
        return [];
      }

      // API returns objects { id, role, is_clinical } — extract the role string
      if (rolesData.length > 0 && typeof rolesData[0] === 'object') {
        return rolesData.map((r: any) => r.role || r.name || String(r));
      }

      return rolesData as string[];
    } catch (error) {
      logger.error('❌ [getRoles] API call failed:', error);
      return [];
    }
  },

  async getRolesMetadata(): Promise<any[]> {
    try {
      const res = await apiGet('/rds/lookups/roles');
      logger.debug('🔍 [getRolesMetadata] response:', res);

      const rolesData = Array.isArray(res)
        ? res
        : Array.isArray(res?.roles)
          ? res.roles
          : null;

      if (Array.isArray(rolesData) && rolesData.length > 0 && typeof rolesData[0] === 'object') {
        return rolesData;
      }

      logger.warn('⚠️ [getRolesMetadata] Unexpected API shape, returning empty list.', res);
      return [];
    } catch (error) {
      logger.error('❌ [getRolesMetadata] API call failed:', error);
      return [];
    }
  },

  async getDashboardSummary() {
    try {
      return await apiGet('/rds/dashboard/summary');
    } catch (error) {
      console.error("Error getting dashboard summary:", error);
      return null;
    }
  },

  async getUnrespondedPatients() {
    try {
      const res = await apiGet('/rds/dashboard/unresponded-patients');
      return (res as any)?.unresponded_patients || [];
    } catch (error) {
      console.error("Error getting unresponded patients:", error);
      return [];
    }
  },

  async markPatientContacted(encounterId: string) {
    try {
      return await apiPost(`/rds/appointments/${encounterId}/mark-contacted`, {});
    } catch (error) {
      console.error("Error marking patient contacted:", error);
      return null;
    }
  },

  async getAppointmentConfirmInfo(token: string) {
    try {
      return await apiGet(`/rds/appointments/confirm-info/${token}`);
    } catch (error) {
      console.error("Error getting confirmation info:", error);
      return null;
    }
  },

  async submitAppointmentConfirmation(token: string, action: string) {
    try {
      return await apiPost('/rds/appointments/confirm-response', { token, action });
    } catch (error) {
      console.error("Error submitting appointment confirmation:", error);
      return null;
    }
  },

  async getAppointments(params: any) {
    try {
      return await apiGet('/rds/appointments', params);
    } catch (error) {
      console.error("Error getting appointments:", error);
      return { data: [], total: 0 };
    }
  },

  async createAppointments(appointments: any[]) {
    try {
      return await apiPost('/rds/appointments', { appointments });
    } catch (error) {
      console.error("Error creating appointments:", error);
      return { error };
    }
  },

  async getPatients(search?: string) {
    try {
      const res = await apiGet('/rds/patients', search ? { search } : undefined);
      return Array.isArray(res) ? res : (res as any)?.data || [];
    } catch (error) {
      console.error("Error getting patients:", error);
      return [];
    }
  },

  async getHospitalsAndBranches() {
    try {
      const res = await apiGet('/rds/lookups/hospitals');
      console.log("🔍 Raw lookup response:", res);
      return Array.isArray(res) ? res : (res as any)?.data || [];
    } catch (error) {
      console.error("Error getting hospitals:", error);
      return [];
    }
  },

  async getPublicHospitalsAndBranches() {
    try {
      const res = await apiGet('/rds/lookups/hospitals/public');
      console.log("🔍 Raw public lookup response:", res);
      return Array.isArray(res) ? res : (res as any)?.data || [];
    } catch (error) {
      console.error("Error getting public hospitals:", error);
      return [];
    }
  },

  async createUserProfile(userId: string, userData: any) {
    if (!userId) return;

    // Use userId (which is username passed from signUp) as auth_uuid
    try {
      await apiPost('/rds/staff-users/create', {
        id: userId,
        auth_uuid: userId,
        name: userData.fullName,
        full_name: userData.fullName,
        email: userData.email,
        role: userData.role,
        location_ids: userData.locationId ? [Number(userData.locationId)] : [],
        associated_branch: userData.locationId ? Number(userData.locationId) : null
      });
      console.log("✅ User profile created in RDS");
    } catch (error) {
      console.error("❌ Failed to create user profile in RDS:", error);
      throw error;
    }
  },

  async getQuestionnaireResponses(params: any) {
    try {
      return await apiGet('/rds/questionnaires/responses', params);
    } catch (error) {
      console.error("Error getting questionnaire responses:", error);
      return [];
    }
  },

  async getQuestionnaireTrend(patientId: string, questionnaireType: string) {
    try {
      return await apiGet('/rds/questionnaires/trend', {
        patient_id: patientId,
        questionnaire_type: questionnaireType
      });
    } catch (error) {
      console.error("Error getting questionnaire trend:", error);
      return [];
    }
  },

  async verifyQuestionnaireAccess(code: string) {
    try {
      return await apiGet('/rds/questionnaires/verify', { code });
    } catch (error) {
      console.error("Error verifying questionnaire access:", error);
      return { error: (error as Error).message };
    }
  },

  // Public patient fetch for SMS questionnaire flow — no JWT required
  async getPublicPatient(patientId: string, accessCode: string) {
    try {
      return await apiGet(`/rds/questionnaires/public/patient/${patientId}`, { code: accessCode });
    } catch (error) {
      console.error("Error fetching public patient:", error);
      return null;
    }
  },

  async savePublicConsent(patientId: string, accessCode: string, consentEntries: any[]) {
    try {
      return await apiPost(`/rds/questionnaires/public/consent`, {
        patient_id: patientId,
        code: accessCode,
        consent_entries: consentEntries,
      });
    } catch (error) {
      console.error("Error saving public consent:", error);
      return null;
    }
  },

  async getStaffByRole(role: string) {
    try {
      const data = await apiGet('/rds/staff-users/role', { role });
      return Array.isArray(data) ? data : (data as any)?.data || [];
    } catch (error) {
      console.error("Error getting staff by role:", error);
      return [];
    }
  },

  async getPatientProviders(patientId: string) {
    try {
      return await apiGet(`/rds/patients/${patientId}/providers`);
    } catch (error) {
      console.error("Error getting patient providers:", error);
      return [];
    }
  },

  async submitQuestionnaireResponse(data: any) {
    try {
      return await apiPost('/rds/questionnaires/responses', data);
    } catch (error) {
      console.error("Error submitting questionnaire response:", error);
      return { error };
    }
  },

  async updateQuestionnaireResponse(id: string, data: any) {
    try {
      return await apiPut(`/rds/questionnaires/responses/${id}`, data);
    } catch (error) {
      console.error("Error updating questionnaire response:", error);
      return { error };
    }
  },

  async getPatient(id: string) {
    try {
      return await apiGet(`/rds/patients/${id}`);
    } catch (error) {
      console.error("Error getting patient:", error);
      return null;
    }
  },

  async createSMSLog(logs: any[]) {
    try {
      return await apiPost('/rds/sms/logs', logs);
    } catch (error) {
      console.error("Error creating SMS log:", error);
      return { error };
    }
  },

  async checkSMSUniquePageId(id: string) {
    try {
      const data = await apiGet('/rds/sms/unique', { id });
      return data.exists;
    } catch (error) {
      console.error("Error checking unique ID:", error);
      return true; // Fail safe: assume exists to prevent usage
    }
  },

  async getSMSLogCount(patientId: string) {
    try {
      const data = await apiGet('/rds/sms/count', { patient_id: patientId });
      return data.count;
    } catch (error) {
      console.error("Error getting SMS log count:", error);
      return 0;
    }
  },

  async updateSMSLog(uniqueId: string, data: any) {
    try {
      return await apiPut(`/rds/sms/logs/unique/${uniqueId}`, data);
    } catch (error) {
      console.error("Error updating SMS log:", error);
      return { error };
    }
  },

  async updatePatient(id: string, data: any) {
    try {
      return await apiPut(`/rds/patients/${id}`, data);
    } catch (error) {
      console.error("Error updating patient:", error);
      throw error;
    }
  },

  async createPatient(data: any) {
    try {
      return await apiPost('/rds/patients', data);
    } catch (error) {
      console.error("Error creating patient:", error);
      throw error;
    }
  },

  async importPatients(patients: any[]) {
    try {
      await apiPost('/rds/patients/import', patients);
    } catch (error) {
      console.error("Error importing patients:", error);
      throw error;
    }
  },

  async getPatientsWithFilters(params: {
    location_id?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    patientIds?: string[];
  }) {
    try {
      const qs: Record<string, string | number> = {};
      if (params.location_id) qs.location_id = params.location_id;
      if (params.search) qs.search = params.search;
      if (params.page != null) qs.page = params.page;
      if (params.pageSize != null) qs.pageSize = params.pageSize;
      if (params.patientIds?.length) qs.ids = params.patientIds.join(',');
      const res = await apiGet<{ data?: any[]; total?: number } | any[]>('/rds/patients', qs);
      if (Array.isArray(res)) return { data: res };
      return { data: (res as any).data || [], total: (res as any).total };
    } catch (error) {
      console.error("Error fetching patients:", error);
      return { data: [] };
    }
  },

  async getPatientEncounters(params: {
    patient_id?: string;
    primary_provider_id?: string;
    secondary_provider_id?: string;
  }) {
    try {
      const qs: Record<string, string> = {};
      if (params.patient_id) qs.patient_id = params.patient_id;
      if (params.primary_provider_id) qs.primary_provider_id = params.primary_provider_id;
      if (params.secondary_provider_id) qs.secondary_provider_id = params.secondary_provider_id;
      const res = await apiGet<any>('/rds/patient-encounters', qs);
      return Array.isArray(res) ? res : (res?.data || []);
    } catch (error) {
      console.error("Error fetching encounters:", error);
      return [];
    }
  },

  async createPatientEncounter(data: any) {
    try {
      return await apiPost('/rds/patient-encounters', data);
    } catch (error) {
      console.error("Error creating encounter:", error);
      throw error;
    }
  },

  async updatePatientEncounter(encounterId: string, data: any) {
    try {
      return await apiPut(`/rds/patient-encounters/${encounterId}`, data);
    } catch (error) {
      console.error("Error updating encounter:", error);
      throw error;
    }
  },

  async getSMSLogsByPatient(patientId: string) {
    try {
      const res = await apiGet<any[]>('/rds/sms/logs', { patient_id: patientId });
      return Array.isArray(res) ? res : [];
    } catch (error) {
      console.error("Error fetching SMS logs:", error);
      return [];
    }
  },

  async getQuestionnaireTemplates(params?: { ids?: string[]; is_active?: boolean }) {
    try {
      const qs: Record<string, string> = {};
      if (params?.ids?.length) qs.ids = params.ids.join(',');
      if (params?.is_active !== undefined) qs.is_active = String(params.is_active);
      const res = await apiGet<any[]>('/rds/questionnaires/templates', qs);
      return Array.isArray(res) ? res : [];
    } catch (error) {
      console.error("Error fetching questionnaire templates:", error);
      return [];
    }
  },

  async sendResendQuestionnaireEmail(body: {
    type: string;
    form_data: any;
    total_score: number;
    questions: any[];
  }) {
    try {
      await apiPost('/rds/email/resend-questionnaire', body);
    } catch (error) {
      console.error("Error sending questionnaire email:", error);
      throw error;
    }
  },

  async getTokenQuestionnaire(token: string) {
    try {
      return await apiGet(`/rds/questionnaires/token/${token}`);
    } catch (error) {
      console.error("Error fetching token questionnaire:", error);
      return null;
    }
  },

  async uploadPatientCSV(file: File) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // As per Route 6 in PATIENTS section of api_curl_reference.md
      return await apiPostMultipart('/rds/patients/import', formData);
    } catch (error) {
      console.error("Error uploading patient CSV:", error);
      throw error;
    }
  },

  async checkCallLog(questionnaireId?: string, patientId?: string, fetchAll: boolean = false) {
    try {
      const params: any = {};
      if (questionnaireId) params.questionnaire_id = questionnaireId;
      if (patientId) params.patient_id = patientId;
      if (fetchAll) params.all = 'true';
      return await apiGet('/rds/calls/check-call-log', params);
    } catch (error) {
      console.error("Error checking call log:", error);
      return { exists: false, logs: [] };
    }
  },

  async scheduleCall(data: any) {
    try {
      return await apiPost('/rds/calls/schedule-a-call-to-patient', data);
    } catch (error) {
      console.error("Error scheduling call:", error);
      throw error;
    }
  },

  async callNow(data: any) {
    try {
      return await apiPost('/rds/calls/call-now', data);
    } catch (error) {
      console.error("Error initiating call:", error);
      throw error;
    }
  },

  async fetchCallNotes(callLogId: number, manualNotes?: string) {
    try {
      return await apiPost(`/rds/calls/fetch-call-notes/${callLogId}`, { manual_notes: manualNotes });
    } catch (error) {
      console.error("Error fetching call notes:", error);
      throw error;
    }
  },

  async uploadCallRecording(audio: File, callLogId: number, manualNotes?: string) {
    try {
      const formData = new FormData();
      formData.append('audio', audio);
      formData.append('call_log_id', String(callLogId));
      if (manualNotes) formData.append('manual_notes', manualNotes);
      return await apiPostMultipart('/rds/calls/call-recording', formData);
    } catch (error) {
      console.error("Error uploading call recording:", error);
      throw error;
    }
  },

  async getCallRecording(callLogId: number, presignSeconds?: number) {
    try {
      const params: any = {};
      if (presignSeconds) params.presign_seconds = presignSeconds;
      return await apiGet(`/rds/calls/call-recording/${callLogId}`, params);
    } catch (error) {
      console.error("Error getting call recording:", error);
      throw error;
    }
  },

  async getStates() {
    try {
      const res = await apiGet('/rds/lookups/states');
      return Array.isArray(res) ? res : (res as any)?.data || [];
    } catch (error) {
      console.error("Error fetching states:", error);
      return [];
    }
  },

  async createHospital(data: any) {
    try {
      return await apiPost('/rds/hospitals/add-hospital', data);
    } catch (error) {
      console.error("Error creating hospital:", error);
      throw error;
    }
  },

  async updateHospital(id: string, data: any) {
    try {
      return await apiPut(`/rds/hospitals/${id}`, data);
    } catch (error) {
      console.error("Error updating hospital:", error);
      throw error;
    }
  },

  async createBranch(hospitalId: string, data: any) {
    try {
      const body = {
        hospital_id: Number(hospitalId),
        state_id: data.state_id,
        name: data.name,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || null,
        city: data.city,
        zip: data.zip,
        email: data.email || null,
        phone: data.phone || null,
      };
      return await apiPost('/rds/hospitals/add-branch', body);
    } catch (error) {
      console.error("Error creating branch:", error);
      throw error;
    }
  },


  async updateBranch(branchId: string, data: any) {
    try {
      return await apiPut(`/rds/branches/${branchId}`, data);
    } catch (error) {
      console.error("Error updating branch:", error);
      throw error;
    }
  },

  async verifyCallAccess(code: string) {
    try {
      return await apiGet(`/rds/calls/call-consent/verify/${code}`);
    } catch (error) {
      console.error("Error verifying call access:", error);
      throw error;
    }
  },

  async updateCallConsent(code: string, consent: 'YES' | 'NO') {
    try {
      return await apiPost(`/rds/calls/call-consent/respond/${code}`, { consent });
    } catch (error) {
      console.error("Error updating call consent:", error);
      throw error;
    }
  },

  async createStaff(data: any) {
    try {
      return await apiPost('/rds/staff-users/create', data);
    } catch (error) {
      console.error("Error creating staff:", error);
      throw error;
    }
  },

  async updateStaff(id: string, data: any) {
    try {
      return await apiPut(`/rds/staff-users/${id}`, data);
    } catch (error) {
      console.error("Error updating staff:", error);
      throw error;
    }
  },

  async getProviders(params?: { location_id?: string | number; branch_id?: string | number }) {
    try {
      const data = await apiGet('/rds/lookups/providers', params);
      return Array.isArray(data) ? data : (data as any)?.data || [];
    } catch (error) {
      console.error("Error getting providers:", error);
      return [];
    }
  },

  async getBranchByLocation(locationId: string | number) {
    try {
      return await apiGet('/rds/lookups/branch-by-location', { location_id: locationId });
    } catch (error) {
      console.error("Error getting branch by location:", error);
      return null;
    }
  },

  async updateMyProfile(data: any) {
    try {
      return await apiPost('/rds/staff-users/me/update', data);
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  // Audio Archive RDS implementations
  async listAudioFiles(params?: any) {
    try {
      const res = await apiGet('/rds/audio', params);
      return res;
    } catch (error) {
      try {
        console.warn("Retrying audio listing with /audio-archives endpoint...", error);
        return await apiGet('/audio-archives', params);
      } catch (fallbackErr) {
        console.error("Error listing audio files:", error);
        return { data: { files: [], count: 0 } };
      }
    }
  },

  async getPatientAudioFiles(patientUuid: string, params?: any) {
    try {
      return await apiGet(`/rds/audio/${patientUuid}`, params);
    } catch (error) {
      console.error(`Error listing audio files for patient ${patientUuid}:`, error);
      return { data: { files: [], count: 0 } };
    }
  },

  async refreshAudioPresignUrl(key: string, expiration?: number) {
    try {
      const params: any = { key };
      if (expiration) params.expiration = expiration;
      return await apiGet('/rds/audio/presign', params);
    } catch (error) {
      try {
        const params: any = { key };
        if (expiration) params.expiration = expiration;
        return await apiGet('/audio-archives/presign', params);
      } catch (fallbackErr) {
        console.error("Error refreshing presign URL:", error);
        throw error;
      }
    }
  },

  async uploadAudioFile(patientUuid: string, file: File, surveyReference: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('survey_reference', surveyReference);
      return await apiPostMultipart(`/rds/audio/${patientUuid}/upload`, formData);
    } catch (error) {
      console.error("Error uploading audio file:", error);
      throw error;
    }
  },

  async deleteAudioFile(patientUuid: string, s3Key: string) {
    try {
      return await apiDelete(`/rds/audio/${patientUuid}`, { s3_key: s3Key });
    } catch (error) {
      try {
        console.warn("Retrying delete with /audio-delete endpoint...");
        const token = await biometricAuthService.getBiometricToken();
        const baseUrl = ENV.VITE_AWS_API_GATEWAY_URL.replace(/\/$/, '');
        const res = await fetch(`${baseUrl}/audio-delete?key=${encodeURIComponent(s3Key)}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
        return await res.json();
      } catch (fallbackErr) {
        console.error("Error deleting audio file:", error);
        throw error;
      }
    }
  },

  // CMS Quality Alerts
  async getCmsAlertStats(params?: { days?: number }) {
    try {
      return await apiGet('/rds/cms-alerts/stats', params);
    } catch (error) {
      console.error("Error fetching CMS alert stats:", error);
      return null;
    }
  },

  async getActiveCmsAlerts(params: {
    patient_id?: string;
    cp_id?: string;       // alias — mapped to provider_id below
    provider_id?: string;
    severity?: string;
    questionnaire_type?: string;
    alert_status?: string; // default 'active'; pass 'resolved' for resolved card
    unbilled?: string;     // pass 'true' to fetch active + acknowledged for unbilled CPT
    page?: number;
    per_page?: number;
  }) {
    try {
      // Backend reads "provider_id" not "cp_id" — remap before sending
      const { cp_id, ...rest } = params;
      const qs = { ...rest, ...(cp_id ? { provider_id: cp_id } : {}) };
      return await apiGet('/rds/cms-alerts/active', qs);
    } catch (error) {
      console.error("Error fetching active CMS alerts:", error);
      return { alerts: [], total: 0 };
    }
  },

  async getPatientCmsAlerts(patientId: string) {
    try {
      return await apiGet(`/rds/cms-alerts/patient/${patientId}`);
    } catch (error) {
      console.error(`Error fetching CMS alerts for patient ${patientId}:`, error);
      return [];
    }
  },

  async acknowledgeCmsAlert(alertId: number) {
    try {
      // Backend route: PUT /cms-alerts/<alert_id>/acknowledge
      return await apiPut(`/rds/cms-alerts/${alertId}/acknowledge`);
    } catch (error) {
      console.error(`Error acknowledging CMS alert ${alertId}:`, error);
      return { success: false, error };
    }
  },

  async dismissCmsAlert(alertId: number) {
    try {
      // Backend route: PUT /cms-alerts/<alert_id>/dismiss
      return await apiPut(`/rds/cms-alerts/${alertId}/dismiss`);
    } catch (error) {
      console.error(`Error dismissing CMS alert ${alertId}:`, error);
      return { success: false, error };
    }
  },

  async resolveCmsAlert(alertId: number, notes: string) {
    try {
      // Backend route: PUT /cms-alerts/<alert_id>/resolve
      // Backend expects "resolution_notes" not "notes"
      return await apiPut(`/rds/cms-alerts/${alertId}/resolve`, { resolution_notes: notes });
    } catch (error) {
      console.error(`Error resolving CMS alert ${alertId}:`, error);
      return { success: false, error };
    }
  },

  async billCmsAlert(alertId: number) {
    try {
      // Backend route: PUT /cms-alerts/<alert_id>/bill
      return await apiPut(`/rds/cms-alerts/${alertId}/bill`);
    } catch (error) {
      console.error(`Error billing CMS alert ${alertId}:`, error);
      return { success: false, error };
    }
  },

  // ── Registration Queue & Lead Self-Registration Methods ─────────────
  async generateRegistrationLink(params: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    send_sms?: boolean;
    send_email?: boolean;
    base_url?: string;
  }) {
    return await apiPost('/rds/registration-queue/generate-link', params);
  },

  async getPublicRegistrationData(token: string) {
    return await apiGet(`/rds/registration-queue/public/${token}`);
  },

  async submitPublicRegistration(token: string, patient_data: any) {
    return await apiPost(`/rds/registration-queue/public/${token}/submit`, { patient_data });
  },

  async getRegistrationQueue(status: string = "submitted", search?: string) {
    return await apiGet('/rds/registration-queue/list', { status, search });
  },

  async getRegistrationQueueItem(id: string) {
    return await apiGet(`/rds/registration-queue/${id}`);
  },

  async resendRegistrationLink(id: string, options?: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    send_sms?: boolean;
    send_email?: boolean;
    preserve_data?: boolean;
  }) {
    return await apiPost(`/rds/registration-queue/${id}/resend`, options || {});
  },

  async completeRegistrationQueueItem(id: string, final_patient_id: string) {
    return await apiPost(`/rds/registration-queue/${id}/complete`, { final_patient_id });
  }
};

export default AuthService;
